/**
 * Share button + membership modal for a tracker. Reuses the SAME
 * `ManageMembersModal` shell that Projects' "Share" uses (via
 * `features/manage-project-members`), so any future change to the modal
 * propagates to both. The tracker data source (members endpoint + workspace
 * users) is injected here.
 *
 * Access model: a tracker with no explicit members is open to the whole
 * workspace; adding people restricts it to the owner, admins, and those
 * people. The owner is always kept.
 */
import { useMemo, useState } from 'react';
import { Button } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { Share2 } from 'lucide-react';
import {
  trackerMembersQueryOptions,
  useUpdateTrackerMembersMutation,
  type Tracker,
  type TrackerAccessLevel,
} from '@/entities/tracker';
import {
  workspaceUsersQuery,
  type ProjectMember,
  type WorkspaceUser,
} from '@/entities/project-member';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ManageMembersModal } from '@/features/manage-project-members';
import { boardTypeOf } from '@/widgets/trackers-board';
import { track } from '@/shared/analytics';
import { notificationService } from '@/lib/services/notification.service';
import { useModalState } from '@/shared/hooks';
import { useTranslation } from 'react-i18next';

const ACCESS_EDIT: TrackerAccessLevel = 'edit';
const ACCESS_VIEW: TrackerAccessLevel = 'view';

export function TrackerShareButton({
  tracker,
  size = 'default',
}: {
  tracker: Tracker;
  /** `lg` is the 40px rung the redesigned board header sits its actions on. */
  size?: 'default' | 'lg';
}) {
  const { t } = useTranslation('trackers');

  const modal = useModalState();
  return (
    <>
      <Button variant="outline" size={size} onClick={modal.open}>
        <Share2 className="size-4" />
        {t('trackers.trackerShareButton.share')}
      </Button>
      {modal.isMounted ? (
        <TrackerShareModal
          key={modal.instanceKey}
          open={modal.isOpen}
          onClose={modal.close}
          tracker={tracker}
        />
      ) : null}
    </>
  );
}

export function TrackerShareModal({
  open,
  onClose,
  tracker,
}: {
  open: boolean;
  onClose: () => void;
  tracker: Tracker;
}) {
  const { t } = useTranslation('trackers');

  const workspaceId = useCurrentWorkspaceId() ?? '';
  const [searchInput, setSearchInput] = useState('');

  // All workspace users (unfiltered): resolves member ids → display rows AND
  // feeds the add picker, which is narrowed here by the typed text.
  const usersQuery = useQuery(workspaceUsersQuery(workspaceId));
  const membersQuery = useQuery(trackerMembersQueryOptions(workspaceId, tracker.id));
  const updateMembers = useUpdateTrackerMembersMutation({ workspaceId, trackerId: tracker.id });

  const allUsers: WorkspaceUser[] = useMemo(() => usersQuery.data?.data ?? [], [usersQuery.data]);
  // A default board belongs to the workspace, so its recorded creator is not pinned as owner.
  const ownerId =
    membersQuery.data?.ownerId || (tracker.defaultKey ? undefined : tracker.createdBy);
  const memberUserIds = membersQuery.data?.userIds ?? [];

  // Current members = explicit members ∪ owner, resolved to display rows.
  const members = useMemo<ProjectMember[]>(() => {
    const ids = [...new Set([...(ownerId ? [ownerId] : []), ...memberUserIds])];
    const byId = new Map(allUsers.map((u) => [u.id, u]));
    return ids.map((id) => {
      const u = byId.get(id);
      return {
        id,
        name: u?.name ?? t('trackers.trackerShareButton.unknownUser'),
        email: u?.email ?? '',
        avatarUrl: u?.avatarUrl ?? null,
        permissions: [],
        createdAt: '',
        updatedAt: '',
      };
    });
  }, [allUsers, ownerId, memberUserIds, t]);

  // Owning the search input switches off TagInput's own filter (it assumes the
  // owner re-queries the roster), so the narrowing has to happen here.
  const pickerUsers = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter(
      (u) => u.name.toLowerCase().includes(q) || (u.email?.toLowerCase().includes(q) ?? false),
    );
  }, [allUsers, searchInput]);

  const isRestricted = memberUserIds.length > 0;
  const hint = isRestricted
    ? t('trackers.trackerShareButton.onlyThesePeopleAndWorkspace')
    : t('trackers.trackerShareButton.anyoneInTheWorkspaceCan');

  // Edit first: someone added here starts on the first level listed.
  const accessLevels = useMemo(
    () => [
      { id: ACCESS_EDIT, roleName: t('trackers.trackerShareButton.canEdit') },
      { id: ACCESS_VIEW, roleName: t('trackers.trackerShareButton.canView') },
    ],
    [t],
  );
  const assignments = useMemo(
    () =>
      (membersQuery.data?.members ?? []).map((m) => ({
        identityId: m.userId,
        roleId: m.accessLevel,
      })),
    [membersQuery.data],
  );

  const handleSave = (memberIds: string[], levels?: Array<{ userId: string; roleId: string }>) => {
    const levelOf = new Map(levels?.map((l) => [l.userId, l.roleId]));
    // Owner-only means "no explicit members" → re-open to the workspace.
    const explicit = memberIds
      .filter((id) => id !== ownerId)
      .map((userId) => ({
        userId,
        accessLevel: levelOf.get(userId) === ACCESS_VIEW ? ACCESS_VIEW : ACCESS_EDIT,
      }));
    updateMembers.mutate(explicit, {
      onSuccess: () => {
        track('tracker_shared', {
          tracker_id: tracker.id,
          board_type: boardTypeOf(tracker.itemSource),
          member_count: explicit.length,
          is_restricted: explicit.length > 0,
        });
        notificationService.showSuccess(t('trackers.trackerShareButton.trackerSharingUpdated'), {
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
        // `onClose` alone — the host unmounts on its own timer.
        onClose();
      },
    });
  };

  return (
    <ManageMembersModal
      open={open}
      onClose={onClose}
      title={t('trackers.trackerShareButton.shareTracker')}
      peopleHeading={t('trackers.trackerShareButton.peopleWithAccess')}
      hint={hint}
      ownerId={ownerId}
      members={members}
      workspaceUsers={pickerUsers}
      isLoadingUsers={usersQuery.isLoading}
      searchInput={searchInput}
      onSearchChange={setSearchInput}
      onSave={handleSave}
      isSaving={updateMembers.isPending}
      saveLabel={t('trackers.trackerShareButton.save')}
      loadError={
        membersQuery.isError
          ? {
              title: t('trackers.trackerShareButton.couldNotLoadAccess'),
              description: t('trackers.trackerShareButton.couldNotLoadAccessDescription'),
              retryLabel: t('trackers.trackerShareButton.retry'),
              onRetry: () => void membersQuery.refetch(),
            }
          : undefined
      }
      roles={{
        enabled: true,
        canEdit: true,
        availableRoles: accessLevels,
        assignments,
        isLoading: membersQuery.isLoading,
        selectorLabel: (name, level) =>
          t('trackers.trackerShareButton.accessLevelFor', { name, level }),
      }}
    />
  );
}
